import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { customerAPI, configAPI, broadbandAPI, catvAPI } from '../../services/api';
import { startOnlinePayment } from '../../utils/onlinePayment';
import { dtdStartsTomorrowForExpiry } from '../../utils/dtdCutoff';
import DtdAfter8pmNotice from '../../components/common/DtdAfter8pmNotice';
import LongTermRegularNotice from '../../components/common/LongTermRegularNotice';
import StbOnNotice from '../../components/common/StbOnNotice';
import { stbOnNoticeApplies } from '../../utils/stbOnNotice';
import { useLongTermAutoStart, longTermRegularNoticeApplies } from '../../utils/longTermNotice';
import toast from 'react-hot-toast';
import { Tv, Wifi, Check, Info, Clock } from 'lucide-react';

const fmt = n => new Intl.NumberFormat('en-IN').format(Math.round(n || 0));

// House-wide default advance-recharge offer — mirrors backend routes/customers.js's
// buildAdvanceTiers exactly (kept in sync manually, same as the CATV DTD 5/10 rule
// elsewhere): 1-5 months at the plain rate, 6 months for the price of 5, 12 months for
// the price of 10. Only used when the plan has no admin-configured pricingTiers of its own.
const ADVANCE_TIER_MONTHS = [1, 2, 3, 4, 5, 6, 12];
const ADVANCE_TIER_DISCOUNT_PAY_MONTHS = { 6: 5, 12: 10 };
function buildAdvanceTiers(plan) {
  return ADVANCE_TIER_MONTHS.map(months => {
    const payMonths = ADVANCE_TIER_DISCOUNT_PAY_MONTHS[months] || months;
    const price = Math.round((plan.price || 0) * payMonths);
    const free = months - payMonths;
    const label = free > 0 ? `${months} Months (Pay ${payMonths}, Get ${free} Free)` : `${months} Month${months > 1 ? 's' : ''}`;
    return { label, months, price };
  });
}

// "9:00 AM" / "7:00 PM" -> { hours, minutes } in 24h, for a same-day comparison only.
function parseClockTime(str) {
  const m = /^(\d{1,2}):(\d{2})\s*(AM|PM)$/i.exec(String(str || '').trim());
  if (!m) return null;
  let hours = parseInt(m[1], 10) % 12;
  if (/PM/i.test(m[3])) hours += 12;
  return { hours, minutes: parseInt(m[2], 10) };
}
function isOutsideOfficeHours(officeHours) {
  if (!officeHours?.enabled) return false;
  const start = parseClockTime(officeHours.start);
  const end = parseClockTime(officeHours.end);
  if (!start || !end) return false;
  const now = new Date();
  const mins = now.getHours() * 60 + now.getMinutes();
  return mins < (start.hours * 60 + start.minutes) || mins > (end.hours * 60 + end.minutes);
}

// Dedicated self-service renewal / advance-recharge flow — pick a duration, pay online,
// expiry extends the moment payment is verified, no staff step in between. Covers both
// 'dtd' (day-to-day) and 'regular' (calendar-month auto-renew) billing (2026-08-01) — a
// regular customer prepaying here simply advances billing.lastBillGeneratedAt, so the
// monthly cron skips every month this payment already covers (same mechanism an agent's
// manual advance collection already uses). Separate from CustomerPlansPage.js (browse/
// switch plans, which still goes through the manual signature + ServiceRequest + self-
// reported UPI path) — this one is fully automated. Covers both CATV and Broadband
// (2026-07-27 — broadband used to be QR-code + screenshot only, kept as an alternative).
//
// Two gateway shapes are supported (see backend POST /me/renew/create-order):
//  - Razorpay: JS-modal checkout, verified via customerAPI.verifyRenewPayment (AJAX).
//  - AblePay: hosted-redirect — the browser is form-POSTed away to AblePay's own payment
//    page entirely, then AblePay POSTs the result back to a backend return_url, which
//    303-redirects here with ?payment=success|failed in the query string (see
//    routes/webhooks.js /ablepay/return) — there is no client-side verify step for it.
export default function CustomerRenewPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const service = searchParams.get('service') === 'broadband' ? 'broadband' : 'catv';
  const isCatv = service === 'catv';

  const [customer, setCustomer] = useState(null);
  const [companyName, setCompanyName] = useState('AngelNet');
  const [officeHours, setOfficeHours] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedMonths, setSelectedMonths] = useState(null);
  const [catalog, setCatalog] = useState([]);
  const [pickedPlanId, setPickedPlanId] = useState(null);
  const [paying, setPaying] = useState(false);
  const [done, setDone] = useState(null); // { receiptNumber }
  const [show8pmNotice, setShow8pmNotice] = useState(false);
  const [showLongTermNotice, setShowLongTermNotice] = useState(false);
  const [showStbNotice, setShowStbNotice] = useState(false);
  const longTermAuto = useLongTermAutoStart();

  useEffect(() => {
    // Customer-visible catalog, only used when the assigned plan has no price (see `unpriced`).
    const listReq = isCatv ? catvAPI.getPackages({ activeOnly: 'true' }) : broadbandAPI.getPlans({ activeOnly: 'true' });
    Promise.all([customerAPI.getMe(), configAPI.get(), listReq.catch(() => null)]).then(([meRes, cfgRes, listRes]) => {
      setCustomer(meRes.data.data);
      setCatalog((listRes?.data?.data || []).filter(p => p.price > 0 && p.validityDays <= 31 && p.type !== 'addon' && p.packageType !== 'addon'));
      if (cfgRes.data?.data?.companyName) setCompanyName(cfgRes.data.data.companyName);
      if (cfgRes.data?.data?.officeHours) setOfficeHours(cfgRes.data.data.officeHours);
    }).catch(() => toast.error('Failed to load account')).finally(() => setLoading(false));

    // Landing back here after AblePay's hosted-redirect flow — see comment above.
    const paymentResult = searchParams.get('payment');
    if (paymentResult === 'success') {
      setDone({ receiptNumber: searchParams.get('receipt') || undefined });
      toast.success('Renewal successful!');
    } else if (paymentResult === 'failed') {
      toast.error(searchParams.get('message') || 'Payment failed — please try again.');
    }
  }, []); // eslint-disable-line

  if (loading) return <div style={{ textAlign: 'center', padding: 60 }}><div className="spinner" style={{ margin: '0 auto' }} /></div>;
  if (!customer) return null;

  const linked = customer.linkedAccount || null;
  const source = isCatv
    ? (customer.customerType !== 'broadband' ? customer : linked)
    : (customer.customerType !== 'catv' ? customer : linked);
  const hasService = source && (source.customerType === service || source.customerType === 'both') && !!source[service];

  if (!hasService) {
    return (
      <div className="card empty-state" style={{ padding: 40 }}>
        <p>You don't have a {isCatv ? 'CableTV' : 'Broadband'} connection on this account.</p>
      </div>
    );
  }

  const durationType = source.billing?.durationType || 'regular';
  const isRegular = durationType !== 'dtd';
  const plan = isCatv ? source.catv?.package : source.broadband?.plan;
  const expiryDate = isCatv ? source.catv?.expiryDate : source.broadband?.expiryDate;

  if (!plan) {
    return (
      <div className="card empty-state" style={{ padding: 40 }}>
        <p>No plan is assigned to this service — please contact support.</p>
      </div>
    );
  }

  // Broadband: the customer may renew onto a different monthly plan from the catalog (server
  // prices it and switches the account once paid). `chosen` is that plan, else the assigned one.
  const pickedPlan = !isCatv ? catalog.find(p => p._id === pickedPlanId) || null : null;
  const chosen = pickedPlan || plan;
  const switching = !!pickedPlan && pickedPlan._id !== plan._id;
  const allTiers = chosen.pricingTiers?.length ? chosen.pricingTiers : buildAdvanceTiers(chosen);
  // CATV duration menu is trimmed per billing type: DTD only offers the 3/6/12-month tiers
  // by default (no 1/2-month top-up) — a specific customer's billing.dtdAllowShortMonths
  // (per-customer toggle on CustomerDetailPage, not a global setting — user rule 2026-08-29)
  // unlocks the full 1/2/3/6/12 set, same as regular. The 6/12-month discounted tiers are
  // locked out for a regular customer with an existing due (source.outstandingBalance) until
  // it's cleared, since they're meant to be a savings play on top of a caught-up account,
  // not a way to pay off an old balance at a discount — DTD's own tiers aren't due-gated.
  // Broadband keeps every plan tier regardless. An STB given on a free-to-use basis
  // (catv.stbFreeToUse, ticked on Customer Detail) is 3/6/12 only, overriding both of the above.
  const CATV_MONTHS = source.catv?.stbFreeToUse ? [3, 6, 12]
    : (isRegular || source.billing?.dtdAllowShortMonths) ? [1, 2, 3, 6, 12] : [3, 6, 12];
  const tiers = isCatv ? allTiers.filter(t => CATV_MONTHS.includes(t.months)) : allTiers;

  const hasDue = isCatv && isRegular && (source.outstandingBalance || 0) > 0;
  const isLocked = t => hasDue && (t.months === 6 || t.months === 12);

  const defaultTier = tiers.find(t => !isLocked(t)) || tiers[0];
  const activeTier = tiers.find(t => t.months === selectedMonths && !isLocked(t)) || defaultTier;

  // Nothing payable until a priced plan is in effect (assigned plan can be a ₹0 legacy import).
  const unpriced = !chosen.pricingTiers?.length && !(chosen.price > 0);
  const showPlanPicker = !isCatv && (unpriced || catalog.length > 0);
  const payTier = activeTier;

  // DTD CableTV after 8 PM with nothing still running: the recharge counts from tomorrow
  // (enforced server-side, utils/dtdCutoff.js) — the customer must accept that first.
  const needs8pmNotice = isCatv && durationType === 'dtd' && dtdStartsTomorrowForExpiry(expiryDate);
  // 6/12 months for a regular customer with the automatic box swap on (utils/longTermNotice.js).
  const needsLongTermNotice = isCatv && longTermRegularNoticeApplies(longTermAuto, durationType, activeTier.months);
  // DTD, or regular but suspended on GTPL: the box must be on for the recharge to land
  // (utils/stbOnNotice.js). Asked first, then the notices above.
  const needsStbNotice = isCatv && stbOnNoticeApplies(source);
  const afterStbNotice = () => {
    setShowStbNotice(false);
    if (needs8pmNotice) setShow8pmNotice(true);
    else if (needsLongTermNotice) setShowLongTermNotice(true);
    else handlePay();
  };
  const onPayClick = () => (needsStbNotice ? setShowStbNotice(true) : afterStbNotice());

  const handlePay = async () => {
    setShow8pmNotice(false);
    setShowLongTermNotice(false);
    setPaying(true);
    try {
      await startOnlinePayment({
        service, companyName,
        months: payTier.months, ...(switching ? { planId: pickedPlan._id } : {}),
        accentColor: isCatv ? '#7c3aed' : '#2563eb',
        description: `${isCatv ? 'CableTV' : 'Broadband'} ${switching ? `${pickedPlan.displayName || pickedPlan.name} —` : 'renewal —'} ${payTier.label || `${payTier.months} month(s)`}`,
        onSuccess: ({ receiptNumber }) => {
          setDone({ receiptNumber });
          toast.success('Renewal successful!');
          setPaying(false);
        },
        onFailure: (message) => {
          if (message) toast.error(message);
          setPaying(false);
        },
      });
    } catch (err) {
      toast.error(err.response?.data?.message || err.message || 'Could not start payment');
      setPaying(false);
    }
  };

  if (done) {
    const afterHours = isOutsideOfficeHours(officeHours);
    return (
      <div className="card" style={{ padding: 32, textAlign: 'center', borderColor: '#bbf7d0' }}>
        <Check size={36} color="#16a34a" style={{ marginBottom: 10 }} />
        <div style={{ fontWeight: 700, fontSize: '1.1rem', marginBottom: 6 }}>Renewed successfully!</div>
        {done.receiptNumber && <p style={{ color: 'var(--text-muted)', fontSize: '.85rem', marginBottom: 16 }}>Receipt {done.receiptNumber}</p>}
        {afterHours && (
          <div style={{
            display: 'flex', gap: 8, textAlign: 'left', alignItems: 'flex-start',
            background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 10, padding: 12, marginBottom: 16,
          }}>
            <Clock size={16} color="#b45309" style={{ flexShrink: 0, marginTop: 1 }} />
            <div style={{ fontSize: '.8rem', color: '#92400e' }}>
              This is outside our office hours ({officeHours.start} – {officeHours.end}). Your payment and validity are
              confirmed already — if this involves a channel/package change, it will reflect once our office reopens.
            </div>
          </div>
        )}
        <button className="btn btn-primary" onClick={() => navigate('/portal')}>Back to Dashboard</button>
      </div>
    );
  }

  const accent = isCatv ? '#7c3aed' : '#2563eb';

  return (
    <div>
      <h2 style={{ fontSize: '1.2rem', fontWeight: 700, marginBottom: 4, display: 'flex', alignItems: 'center', gap: 8 }}>
        {isCatv ? <Tv size={18} color={accent} /> : <Wifi size={18} color={accent} />} Renew {isCatv ? 'CableTV' : 'Broadband'}
      </h2>
      <p style={{ color: 'var(--text-muted)', fontSize: '.85rem', marginBottom: 12 }}>
        {chosen.displayName || chosen.name} · Pay online and your service extends immediately.
      </p>

      {isRegular && (
        <div style={{
          display: 'flex', gap: 8, alignItems: 'flex-start',
          background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 10, padding: 12, marginBottom: 16,
        }}>
          <Info size={16} color="var(--primary)" style={{ flexShrink: 0, marginTop: 1 }} />
          <div style={{ fontSize: '.8rem', color: 'var(--text)' }}>
            This service already auto-renews every month — no action needed. Pay in advance below only if you'd
            like to lock in savings on a longer duration.
          </div>
        </div>
      )}

      {hasDue && (
        <div style={{
          display: 'flex', gap: 8, alignItems: 'flex-start',
          background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 10, padding: 12, marginBottom: 16,
        }}>
          <Info size={16} color="#b45309" style={{ flexShrink: 0, marginTop: 1 }} />
          <div style={{ fontSize: '.8rem', color: '#92400e' }}>
            You have a pending balance of ₹{fmt(source.outstandingBalance)}. Clear it first to unlock the
            discounted 6 &amp; 12 month recharge options.
          </div>
        </div>
      )}

      {showPlanPicker && (
        <div className="card" style={{ padding: 18, marginBottom: 16 }}>
          <div style={{ fontSize: '.68rem', fontWeight: 700, letterSpacing: '.05em', textTransform: 'uppercase', color: 'var(--text-muted)', marginBottom: 10 }}>
            Choose your plan
          </div>
          <select className="form-control" value={pickedPlanId || ''} onChange={e => setPickedPlanId(e.target.value || null)} style={{ width: '100%' }}>
            <option value="">{unpriced ? 'Select a plan…' : `${plan.displayName || plan.name} (current)`}</option>
            {catalog.filter(p => p._id !== plan._id).map(p => <option key={p._id} value={p._id}>{p.displayName || p.name} — ₹{fmt(p.price)}</option>)}
          </select>
        </div>
      )}

      {!unpriced && (
      <div className="card" style={{ padding: 18, marginBottom: 16 }}>
        <div style={{ fontSize: '.68rem', fontWeight: 700, letterSpacing: '.05em', textTransform: 'uppercase', color: 'var(--text-muted)', marginBottom: 10 }}>
          Choose duration
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: 10 }}>
          {tiers.map(t => {
            const sel = t.months === activeTier.months;
            const locked = isLocked(t);
            return (
              <button key={t.months} disabled={locked} onClick={() => !locked && setSelectedMonths(t.months)} style={{
                padding: '12px 10px', borderRadius: 12, textAlign: 'left',
                cursor: locked ? 'not-allowed' : 'pointer',
                opacity: locked ? 0.5 : 1,
                border: sel ? `2px solid ${accent}` : '1px solid var(--border)',
                background: sel ? `${accent}0f` : 'transparent',
              }}>
                <div style={{ fontWeight: 700, fontSize: '.85rem' }}>{t.label || `${t.months} month${t.months > 1 ? 's' : ''}`}</div>
                <div style={{ fontWeight: 800, fontSize: '1.1rem', color: accent }}>₹{fmt(t.price)}</div>
                {locked && <div style={{ fontSize: '.65rem', color: '#b45309', marginTop: 2 }}>Clear dues to unlock</div>}
              </button>
            );
          })}
        </div>
      </div>
      )}

      <div className="card" style={{ padding: 18, marginBottom: 20 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '.9rem', marginBottom: 6 }}>
          <span style={{ color: 'var(--text-muted)' }}>Amount</span>
          <span style={{ fontWeight: 700 }}>₹{fmt(payTier.price)}</span>
        </div>
        {expiryDate && (
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '.8rem', color: 'var(--text-muted)' }}>
            <span>Current validity</span>
            <span>{new Date(expiryDate).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}</span>
          </div>
        )}
      </div>

      <button className="btn btn-primary" disabled={paying || unpriced} onClick={onPayClick} style={{ width: '100%', padding: '12px 0', justifyContent: 'center' }}>
        {paying ? 'Processing…' : `Pay ₹${fmt(payTier.price)} & Renew`}
      </button>
      {showStbNotice && <StbOnNotice onConfirm={afterStbNotice} onCancel={() => setShowStbNotice(false)} />}
      {show8pmNotice && <DtdAfter8pmNotice onConfirm={handlePay} onCancel={() => setShow8pmNotice(false)} />}
      {showLongTermNotice && <LongTermRegularNotice months={activeTier.months} onConfirm={handlePay} onCancel={() => setShowLongTermNotice(false)} />}
    </div>
  );
}
